import { useMemo, useState } from 'react';
import { TrashIcon, PencilSquareIcon, PlusIcon } from '@heroicons/react/24/outline';
import { WizardStepper } from '@/components/Video/WizardStepper';
import { HelpNote } from '@/components/Video/HelpNote';
import { TargetPlatform } from '@/services/clipGenerationApi';
import { PLATFORMS } from '@/pages/Clips/ClipGeneratorPage';
import type { SegmentRow } from '@/pages/Clips/ClipGeneratorPage';
import VideoLibraryPicker, { SelectedVideo } from '@/components/Clips/VideoLibraryPicker';
import ClipDetailsModal from '@/components/Clips/ClipDetailsModal';
import { targetLanguages } from '@/utils/videoTranslationConfig';
import LocalizedTextArea from '@/components/Common/LocalizedTextArea';

function formatClipTime(seconds: number) {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

const STEP_LABELS = [
  { label: 'Source', hint: 'Video & transcript' },
  { label: 'Platforms', hint: 'Where clips will be posted' },
  { label: 'Clip settings', hint: 'Length, silence & segments' },
  { label: 'Options', hint: 'Editing enhancements' },
  { label: 'Review', hint: 'Confirm & generate' },
];

interface ClipOptions {
  faceTracking: boolean;
  autoZoom: boolean;
  autoSubtitles: boolean;
  animatedCaptions: boolean;
  noiseReduction: boolean;
  musicRecommendation: boolean;
  emotionDetection: boolean;
}

interface ClipGeneratorWizardProps {
  title: string;
  setTitle: (v: string) => void;
  selectedVideo: SelectedVideo | null;
  onSelectVideo: (item: SelectedVideo) => void;
  onClearVideo: () => void;
  transcript: string;
  setTranscript: (v: string) => void;
  language: string;
  setLanguage: (v: string) => void;
  // Set when the selected video is a translated/dubbed output — its language
  // is fixed to that translation's target language, not user-selectable.
  lockedLanguage?: string | null;
  keywords: string;
  setKeywords: (v: string) => void;
  selectedPlatforms: TargetPlatform[];
  togglePlatform: (platform: TargetPlatform) => void;
  outputCounts: Record<TargetPlatform, number>;
  setOutputCounts: React.Dispatch<React.SetStateAction<Record<TargetPlatform, number>>>;
  minClipLength: number;
  setMinClipLength: (v: number) => void;
  maxClipLength: number;
  setMaxClipLength: (v: number) => void;
  silenceThresholdDb: number;
  setSilenceThresholdDb: (v: number) => void;
  segmentScope: 'all' | TargetPlatform;
  setSegmentScope: (v: 'all' | TargetPlatform) => void;
  segmentsByScope: Record<string, SegmentRow[]>;
  upsertSegmentRow: (scope: string, index: number | null, row: SegmentRow) => void;
  removeSegmentRow: (scope: string, index: number) => void;
  options: ClipOptions;
  setOptions: React.Dispatch<React.SetStateAction<ClipOptions>>;
  loading: boolean;
  // Resolves to whether the job actually started, so the wizard knows
  // whether to jump back to step 0.
  onGenerate: () => Promise<boolean>;
  onCancel: () => void;
}

export default function ClipGeneratorWizard(props: ClipGeneratorWizardProps) {
  const {
    title, setTitle,
    selectedVideo, onSelectVideo, onClearVideo,
    transcript, setTranscript,
    language, setLanguage,
    lockedLanguage,
    keywords, setKeywords,
    selectedPlatforms, togglePlatform,
    outputCounts, setOutputCounts,
    minClipLength, setMinClipLength,
    maxClipLength, setMaxClipLength,
    silenceThresholdDb, setSilenceThresholdDb,
    segmentScope, setSegmentScope,
    segmentsByScope, upsertSegmentRow, removeSegmentRow,
    options, setOptions,
    loading, onGenerate, onCancel,
  } = props;

  const [step, setStep] = useState(0);
  const maxReached = step;

  // Clip-details modal state for the manual-segment editor (Step 3). `editingIndex`
  // is null when defining a brand-new segment, or the row index being edited.
  // `draftSeed` forces the modal to remount with fresh initial values when
  // "Create Another Clip" is used, rather than adding imperative reset logic
  // inside the modal itself.
  const [segmentModalOpen, setSegmentModalOpen] = useState(false);
  const [editingSegmentIndex, setEditingSegmentIndex] = useState<number | null>(null);
  const [segmentDraftSeed, setSegmentDraftSeed] = useState(0);
  const [segmentDraftInitial, setSegmentDraftInitial] = useState({ start: 0, end: 20, title: '' });

  const openAddSegmentModal = () => {
    setEditingSegmentIndex(null);
    setSegmentDraftInitial({ start: 0, end: 20, title: '' });
    setSegmentDraftSeed((s) => s + 1);
    setSegmentModalOpen(true);
  };

  const openEditSegmentModal = (index: number) => {
    const row = (segmentsByScope[segmentScope] || [])[index];
    if (!row) return;
    setEditingSegmentIndex(index);
    setSegmentDraftInitial({ start: Number(row.start) || 0, end: Number(row.end) || 0, title: row.title });
    setSegmentDraftSeed((s) => s + 1);
    setSegmentModalOpen(true);
  };

  const closeSegmentModal = () => setSegmentModalOpen(false);

  const saveSegmentDraft = (draft: { start: number; end: number; title: string }) => {
    upsertSegmentRow(segmentScope, editingSegmentIndex, {
      start: String(draft.start),
      end: String(draft.end),
      title: draft.title,
    });
  };

  const createAnotherSegment = (draft: { start: number; end: number; title: string }) => {
    saveSegmentDraft(draft);
    setEditingSegmentIndex(null);
    setSegmentDraftInitial({ start: draft.end, end: draft.end + 20, title: '' });
    setSegmentDraftSeed((s) => s + 1);
  };

  const handleCancel = () => {
    onCancel();
    setStep(0);
  };

  const canContinue = useMemo(() => {
    switch (step) {
      case 0:
        return !!selectedVideo;
      case 1:
        return selectedPlatforms.length > 0;
      default:
        return true;
    }
  }, [step, selectedVideo, selectedPlatforms]);

  const goNext = () => setStep((s) => Math.min(STEP_LABELS.length - 1, s + 1));
  const goBack = () => setStep((s) => Math.max(0, s - 1));

  const handleGenerateClick = async () => {
    const started = await onGenerate();
    if (started) setStep(0);
  };

  const renderSourceStep = () => (
    <div className="space-y-4">
      <StepHead title="Add your source" sub="Pick one of your videos to pull clips from." />

      <div className="space-y-1.5">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Video</label>
        <VideoLibraryPicker selected={selectedVideo} onSelect={onSelectVideo} onClear={onClearVideo} />
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-1.5">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Title / video name</label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="My video"
            className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-900 dark:text-white"
          />
        </div>
        <div className="space-y-1.5">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Language</label>
          {lockedLanguage ? (
            <div className="w-full rounded-md border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 px-3 py-2 text-sm text-gray-700 dark:text-gray-300">
              {targetLanguages.find((l) => l.code === lockedLanguage)?.name || lockedLanguage}
              <span className="ml-1.5 text-xs text-gray-400 dark:text-gray-500">(translated video)</span>
            </div>
          ) : (
            <select
              value={language}
              onChange={(e) => setLanguage(e.target.value)}
              className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-900 dark:text-white"
            >
              {targetLanguages.map((l) => (
                <option key={l.code} value={l.code}>
                  {l.name}
                </option>
              ))}
            </select>
          )}
        </div>
      </div>

      <div className="space-y-1.5">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
          {lockedLanguage ? "Transcript (from this video's translation)" : 'Transcript (optional)'}
        </label>
        <LocalizedTextArea
          language={lockedLanguage || language}
          value={transcript}
          onChange={(e) => setTranscript(e.target.value)}
          rows={3}
          placeholder="Paste transcript for better highlight detection..."
          className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-900 dark:text-white"
        />
      </div>

      <div className="space-y-1.5">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Keywords to detect (optional)</label>
        <input
          type="text"
          value={keywords}
          onChange={(e) => setKeywords(e.target.value)}
          placeholder="product, tutorial, sale"
          className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-900 dark:text-white"
        />
      </div>

      <HelpNote>Paste a transcript or keywords when available — it sharpens highlight detection and improves clip selection.</HelpNote>
    </div>
  );

  const renderPlatformsStep = () => (
    <div className="space-y-4">
      <StepHead title="Choose target platforms" sub="Pick where clips will be published and how many to generate for each." />

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {PLATFORMS.map((p) => (
          <label
            key={p.id}
            className={`flex items-center justify-between gap-3 rounded-lg border px-3 py-2 transition-colors cursor-pointer ${
              selectedPlatforms.includes(p.id)
                ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20'
                : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50'
            }`}
          >
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={selectedPlatforms.includes(p.id)}
                onChange={() => togglePlatform(p.id)}
                className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
              />
              <span className="text-sm font-medium text-gray-800 dark:text-gray-200">{p.label}</span>
            </div>
            <input
              type="number"
              min={1}
              max={50}
              value={outputCounts[p.id]}
              onChange={(e) =>
                setOutputCounts((prev) => ({
                  ...prev,
                  [p.id]: Math.max(1, Math.min(50, Number(e.target.value) || 1)),
                }))
              }
              className="w-16 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-2 py-1 text-right text-sm text-gray-900 dark:text-white"
              onClick={(e) => e.stopPropagation()}
            />
          </label>
        ))}
      </div>

      <p className="text-xs text-gray-500 dark:text-gray-400">
        Estimated output:{' '}
        {selectedPlatforms.length > 0
          ? selectedPlatforms
              .map((p) => `${outputCounts[p]} ${PLATFORMS.find((x) => x.id === p)?.label}`)
              .join(', ')
          : 'Select at least one platform'}
      </p>
    </div>
  );

  const renderClipSettingsStep = () => (
    <div className="space-y-5">
      <StepHead title="Clip settings" sub="Control clip length, silence detection, and optionally set exact cut points." />

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="space-y-1.5">
          <label htmlFor="clip-min-length" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Min clip length (s)</label>
          <input
            id="clip-min-length"
            type="number"
            min={3}
            max={180}
            value={minClipLength}
            onChange={(e) => setMinClipLength(Math.max(3, Math.min(180, Number(e.target.value) || 3)))}
            className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-900 dark:text-white"
          />
        </div>
        <div className="space-y-1.5">
          <label htmlFor="clip-max-length" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Max clip length (s)</label>
          <input
            id="clip-max-length"
            type="number"
            min={5}
            max={180}
            value={maxClipLength}
            onChange={(e) => setMaxClipLength(Math.max(5, Math.min(180, Number(e.target.value) || 5)))}
            className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-900 dark:text-white"
          />
        </div>
        <div className="space-y-1.5">
          <label
            htmlFor="clip-silence-threshold"
            className="block text-sm font-medium text-gray-700 dark:text-gray-300"
            title="Audio quieter than this is treated as silence when finding natural clip boundaries"
          >
            Silence threshold (dB)
          </label>
          <input
            id="clip-silence-threshold"
            type="number"
            min={-60}
            max={-10}
            value={silenceThresholdDb}
            onChange={(e) => setSilenceThresholdDb(Math.max(-60, Math.min(-10, Number(e.target.value) || -40)))}
            className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-900 dark:text-white"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Custom clip segments (optional)
        </label>
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
          Specify exact start/end times (in seconds) instead of letting AI auto-detect highlights. The
          &quot;All platforms&quot; list applies to any selected platform below that doesn&apos;t have its own
          tab filled in — e.g. set different cut points for YouTube vs Instagram by switching tabs.
        </p>
        <div className="flex flex-wrap gap-2 mb-3">
          <button
            type="button"
            onClick={() => setSegmentScope('all')}
            className={`px-2.5 py-1 text-xs rounded-full border ${
              segmentScope === 'all'
                ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20 text-indigo-700 dark:text-indigo-300'
                : 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300'
            }`}
          >
            All platforms {(segmentsByScope['all']?.length ?? 0) > 0 ? `(${segmentsByScope['all']!.length})` : ''}
          </button>
          {selectedPlatforms.map((p) => (
            <button
              key={p}
              type="button"
              onClick={() => setSegmentScope(p)}
              className={`px-2.5 py-1 text-xs rounded-full border ${
                segmentScope === p
                  ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20 text-indigo-700 dark:text-indigo-300'
                  : 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300'
              }`}
            >
              {PLATFORMS.find((x) => x.id === p)?.label} {(segmentsByScope[p]?.length ?? 0) > 0 ? `(${segmentsByScope[p]!.length})` : ''}
            </button>
          ))}
        </div>

        <div className="space-y-2">
          {(segmentsByScope[segmentScope] || []).map((row, index) => (
            <div
              key={index}
              className="flex items-center justify-between gap-3 rounded-lg border border-gray-200 dark:border-gray-700 px-3 py-2"
            >
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                  {row.title || 'Untitled clip'}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {formatClipTime(Number(row.start) || 0)}–{formatClipTime(Number(row.end) || 0)}
                </p>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <button
                  type="button"
                  onClick={() => openEditSegmentModal(index)}
                  title="Edit clip"
                  className="p-1.5 rounded-full text-gray-400 hover:text-gray-700 hover:bg-gray-100 dark:hover:text-gray-200 dark:hover:bg-gray-700"
                >
                  <PencilSquareIcon className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={() => removeSegmentRow(segmentScope, index)}
                  title="Remove clip"
                  className="p-1.5 rounded-full text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20"
                >
                  <TrashIcon className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
          <button
            type="button"
            onClick={openAddSegmentModal}
            disabled={!selectedVideo}
            className="inline-flex items-center gap-1 text-xs font-medium text-indigo-600 dark:text-indigo-400 hover:underline disabled:opacity-50 disabled:cursor-not-allowed disabled:no-underline"
          >
            <PlusIcon className="w-3.5 h-3.5" />
            Add clip{segmentScope !== 'all' ? ` for ${PLATFORMS.find((x) => x.id === segmentScope)?.label}` : ''}
          </button>
        </div>
        {(segmentsByScope[segmentScope]?.length ?? 0) > 0 && (
          <p className="mt-2 text-[11px] text-amber-600 dark:text-amber-400">
            Output count/highlight detection for {segmentScope === 'all' ? 'platforms using this list' : PLATFORMS.find((x) => x.id === segmentScope)?.label} will be ignored — exactly one clip is produced per segment above.
          </p>
        )}
      </div>

      {segmentModalOpen && selectedVideo && (
        <ClipDetailsModal
          key={segmentDraftSeed}
          videoUrl={selectedVideo.url}
          initialStart={segmentDraftInitial.start}
          initialEnd={segmentDraftInitial.end}
          initialTitle={segmentDraftInitial.title}
          mode={editingSegmentIndex === null ? 'create' : 'edit'}
          onSave={saveSegmentDraft}
          onCreateAnother={createAnotherSegment}
          onClose={closeSegmentModal}
        />
      )}
    </div>
  );

  const renderOptionsStep = () => (
    <div className="space-y-4">
      <StepHead title="Editing enhancements" sub="Toggle automatic enhancements applied to every generated clip." />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {Object.entries(options).map(([key, value]) => (
          <label key={key} className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 rounded-lg border border-gray-200 dark:border-gray-700 px-3 py-2">
            <input
              type="checkbox"
              checked={value}
              onChange={(e) => setOptions((prev) => ({ ...prev, [key]: e.target.checked }))}
              className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
            />
            {key.replace(/([A-Z])/g, ' $1').replace(/^./, (s) => s.toUpperCase())}
          </label>
        ))}
      </div>
    </div>
  );

  const renderReviewStep = () => {
    const summary: { label: string; value: string }[] = [
      { label: 'Title', value: title || '—' },
      { label: 'Source', value: selectedVideo?.name || '—' },
      { label: 'Language', value: language },
      { label: 'Platforms', value: selectedPlatforms.length > 0 ? selectedPlatforms.map((p) => PLATFORMS.find((x) => x.id === p)?.label).join(', ') : '—' },
      { label: 'Clip length', value: `${minClipLength}s – ${maxClipLength}s` },
      { label: 'Enhancements', value: Object.entries(options).filter(([, v]) => v).map(([k]) => k.replace(/([A-Z])/g, ' $1').replace(/^./, (s) => s.toUpperCase())).join(', ') || 'None' },
    ];

    return (
      <div className="space-y-5">
        <StepHead title="Review & generate" sub="Everything below is editable — click any step above to change it." />

        {!canContinue && step === 1 && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800 dark:border-red-900/40 dark:bg-red-900/20 dark:text-red-200">
            Select at least one target platform.
          </div>
        )}

        <dl className="divide-y divide-gray-200 rounded-xl border border-gray-200 dark:divide-gray-700 dark:border-gray-700">
          {summary.map((row) => (
            <div key={row.label} className="flex items-center justify-between gap-4 px-4 py-2.5 text-sm">
              <dt className="text-gray-500 dark:text-gray-400">{row.label}</dt>
              <dd className="text-right font-medium text-gray-900 dark:text-white truncate max-w-[60%]">{row.value}</dd>
            </div>
          ))}
        </dl>

        <div className="flex items-center justify-end gap-3 border-t border-gray-200 pt-4 dark:border-gray-700">
          <button
            type="button"
            onClick={handleCancel}
            disabled={loading}
            className="inline-flex items-center gap-1 rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={loading || selectedPlatforms.length === 0 || !selectedVideo}
            onClick={handleGenerateClick}
            className="inline-flex items-center gap-1 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Starting...' : 'Generate Clips'}
          </button>
        </div>
      </div>
    );
  };

  const renderStepContent = () => {
    switch (step) {
      case 0:
        return renderSourceStep();
      case 1:
        return renderPlatformsStep();
      case 2:
        return renderClipSettingsStep();
      case 3:
        return renderOptionsStep();
      case 4:
        return renderReviewStep();
      default:
        return null;
    }
  };

  return (
    <div className="space-y-6 bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700">
      <WizardStepper steps={STEP_LABELS} current={step} maxReached={maxReached} onJump={setStep} />

      <div className="mt-2 h-1 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
        <div
          className="h-full rounded-full bg-indigo-600 transition-all duration-500 dark:bg-indigo-500"
          style={{ width: `${((step + (canContinue ? 1 : 0.35)) / STEP_LABELS.length) * 100}%` }}
        />
      </div>

      <div className="animate-fade-in min-h-[420px]">{renderStepContent()}</div>

      {step < STEP_LABELS.length - 1 && (
        <div className="flex items-center justify-between gap-3 border-t border-gray-200 pt-4 dark:border-gray-700">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleCancel}
              className="inline-flex items-center gap-1 rounded-lg px-3 py-2 text-sm font-medium text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={goBack}
              disabled={step === 0}
              className="inline-flex items-center gap-1 rounded-lg px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed dark:text-gray-300 dark:hover:bg-gray-700"
            >
              Back
            </button>
          </div>
          <button
            type="button"
            onClick={goNext}
            disabled={!canContinue}
            className="inline-flex items-center gap-1 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Continue
          </button>
        </div>
      )}
    </div>
  );
}

function StepHead({ title, sub }: { title: string; sub: string }) {
  return (
    <div>
      <h2 className="text-xl font-semibold tracking-tight text-gray-900 dark:text-white">{title}</h2>
      <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{sub}</p>
    </div>
  );
}
